// article-api — 文章(UGC)前端数据层。
//
// 读路径走 PostgREST 视图(view-first 强约束:禁直查物理表 article):
//   - v_article_feed   首页文章流(已发布;可 ?category_id / ?user_id 筛)
//   - v_article_public 文章详情(已发布,带 content)
//   - v_article_mine   我的文章(含草稿/已下架,RLS 限本人)
// 写路径走 /gapi handler(create/update/delete);content 校验 / 嵌入 uuid 校验在 service。
// 嵌入解析:fileRef → rpc('get_public_files'),packRef → v_resource_pack_public_meta(article-render)。
//
// 契约源:docs/api-list/user/articles.md。

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, getSupabase } from '@dogdisk/shared'
import type { JSONContent } from '@tiptap/core'
import type { Module } from './discover-api'
import { getForumIdentity } from './forum-device-id'

export type ArticleStatus = 'draft' | 'published' | 'removed'

export type ArticleFeedItem = {
  id: string
  title: string
  excerpt: string | null
  cover_thumb_url: string | null
  category_id: string | null
  category_name: string | null
  user_id: string
  owner_username: string | null
  owner_nickname: string | null
  owner_avatar: string | null
  like_count: number
  dislike_count: number
  published_at: string | null
}

export type ArticlePublic = ArticleFeedItem & {
  content: JSONContent
  created_at: string
}

export type ArticleMineItem = {
  id: string
  title: string
  excerpt: string | null
  content: JSONContent | null
  cover_file_info_id: string | null
  category_id: string | null
  status: ArticleStatus
  like_count: number
  dislike_count: number
  published_at: string | null
  created_at: string
  updated_at: string
}

export const FEED_COLS =
  'id,title,excerpt,cover_thumb_url,category_id,category_name,user_id,owner_username,owner_nickname,owner_avatar,like_count,dislike_count,published_at'

const PUBLIC_COLS = `${FEED_COLS},content,created_at`

const MINE_COLS =
  'id,title,excerpt,content,cover_file_info_id,category_id,status,like_count,dislike_count,published_at,created_at,updated_at'

/** 首页文章流排序(LNMX9 SortTabRow):最热(like_count)/ 最新(published_at)。 */
export type ArticleFeedSort = 'hot' | 'new'

/** forum 文章流分页页大小(与后端 count 端点共识的每页条数)。 */
const PAGE_SIZE = 20

/** useArticleFeed 分页结果:当前页文章 + 总数(总数仅 forum 走 count 端点)。
 *  page:最热端点回传后端分配的起始页(最新路径不带,为 undefined)。 */
export type ArticleFeedPage = { items: ArticleFeedItem[]; total: number; page?: number }

type ForumHotResp = { page: number; total: number; items: ArticleFeedItem[] }

/** 最热:走后端 /gapi/public/forum/hot(Redis 快照 + 每用户每日随机起始页)。
 *  page 省略 → 后端按身份算起始页并在响应回传;显式 page → 取该页。 */
export async function fetchForumHotPage(args: {
  categoryId?: string
  categoryIds?: string[]
  page?: number
}): Promise<ArticleFeedPage> {
  const p = new URLSearchParams()
  p.set('id', getForumIdentity())
  if (args.categoryIds && args.categoryIds.length > 0) p.set('category_ids', args.categoryIds.join(','))
  else if (args.categoryId) p.set('category_id', args.categoryId)
  if (args.page && args.page > 0) p.set('page', String(args.page))
  const resp = await apiClient.get<ForumHotResp>(`/gapi/public/forum/hot?${p.toString()}`)
  return { items: resp.items ?? [], total: resp.total ?? 0, page: resp.page }
}

// applyCategoryFilter — 给 v_article_feed 查询应用分类过滤。
// categoryIds(多 id,L1 聚合其子类) → .in;categoryId(单 id) → .eq;都空 → 不过滤。
// 抽成纯函数:既复用于 hook,又能脱离 supabase 真连接单测(传入录调用的 mock builder)。
export function applyCategoryFilter<
  T extends { eq: (c: string, v: string) => T; in: (c: string, v: string[]) => T },
>(q: T, args: { categoryId?: string | undefined; categoryIds?: string[] | undefined }): T {
  if (args.categoryIds && args.categoryIds.length > 0) return q.in('category_id', args.categoryIds)
  if (args.categoryId) return q.eq('category_id', args.categoryId)
  return q
}

/**
 * 首页文章流(已发布;可按分类筛 + 排序 + module 过滤)。anon + auth 均可。
 * sort='hot'(默认,对齐 LNMX9 默认选中「最热」)按 like_count 降序、published_at 兜底;
 * sort='new' 按 published_at 降序。
 * module 传值时只返回该模块(forum/dating/game)的文章;不传则不过滤。
 * 分类筛:categoryIds(L1 聚合其全部 L2 子类)走 .in;单 categoryId 走 .eq。
 */
export function useArticleFeed(
  args: {
    categoryId?: string
    categoryIds?: string[]
    module?: Module
    sort?: ArticleFeedSort
    page?: number
    enabled?: boolean
  } = {},
) {
  const { categoryId, categoryIds, module, sort = 'hot', page = 1, enabled = true } = args
  return useQuery({
    queryKey: [
      'article',
      'feed',
      {
        categoryId: categoryId ?? null,
        categoryIds: categoryIds && categoryIds.length > 0 ? [...categoryIds].sort() : null,
        module: module ?? null,
        sort,
        page,
      },
    ] as const,
    enabled,
    queryFn: async (): Promise<ArticleFeedPage> => {
      // 最热:走后端 /gapi/public/forum/hot(Redis 快照 + 每用户每日随机起始页);forum-only
      // (后端固定 module='forum')。page=0 哨兵 → 后端按身份算起始页并回传(forum.tsx 采纳)。
      if (sort === 'hot') {
        return fetchForumHotPage({
          ...(categoryId ? { categoryId } : {}),
          ...(categoryIds && categoryIds.length > 0 ? { categoryIds } : {}),
          ...(page && page > 0 ? { page } : {}),
        })
      }
      // ——以下为 'new' 直查路径(按 published_at 降序;保持不变)——
      const sb = getSupabase()
      let q = sb.from('v_article_feed').select(FEED_COLS)
      q = q.order('published_at', { ascending: false })
      q = q.order('id', { ascending: false }) // 稳定 tiebreaker,避免翻页 range 漂移
      const from = (page - 1) * PAGE_SIZE
      q = q.range(from, from + PAGE_SIZE - 1)
      q = applyCategoryFilter(q, { categoryId, categoryIds })
      if (module) q = q.eq('module', module)
      const { data, error } = await q
      if (error) throw error
      const items = (data ?? []) as ArticleFeedItem[]
      const total = await fetchForumCount({ categoryId, categoryIds, module })
      return { items, total }
    },
  })
}

// fetchForumCount — forum 总数走后端 Redis 缓存端点(避免 PostgREST count 每次压库)。
// category 口径与 feed 的 applyCategoryFilter 对齐:有 categoryIds(L1 聚合 = L1+全部 L2 子类)
// → category_ids(后端 ANY 聚合);否则单 categoryId(L2 叶子);都无 → 全部 forum。
// 仅 module==='forum' 时取真实 count;其余 module 返回 0(调用方按未知/单页处理)。
//
// 端点为匿名可访问(user 组 public 段),未登录用户也能取数。但 count 只用于分页,属
// 非关键路径:任何失败(网络抖动 / 5xx / 偶发 401)都只降级返回 0,绝不抛出——否则会
// 拖垮整个 useArticleFeed 查询,导致 /#/forum 文章流整页报错(回归保护)。
async function fetchForumCount(args: {
  categoryId?: string | undefined
  categoryIds?: string[] | undefined
  module?: Module | undefined
}): Promise<number> {
  if (args.module !== 'forum') return 0
  let qs = ''
  if (args.categoryIds && args.categoryIds.length > 0) {
    qs = `?category_ids=${args.categoryIds.map(encodeURIComponent).join(',')}`
  } else if (args.categoryId) {
    qs = `?category_id=${encodeURIComponent(args.categoryId)}`
  }
  try {
    const resp = await apiClient.get<{ count: number }>(`/gapi/public/articles/count${qs}`)
    return resp.count
  } catch {
    // count 失败不影响文章流渲染:降级为 0(分页器按单页/未知处理)。
    return 0
  }
}

/** 文章详情(已发布,带 content)。anon + auth。 */
export function useArticlePublic(id: string | undefined) {
  return useQuery({
    queryKey: ['article', 'public', id] as const,
    enabled: Boolean(id),
    queryFn: async (): Promise<ArticlePublic | null> => {
      const sb = getSupabase()
      const { data, error } = await sb
        .from('v_article_public')
        .select(PUBLIC_COLS)
        .eq('id', id!)
        .maybeSingle()
      if (error) throw error
      return (data as ArticlePublic | null) ?? null
    },
  })
}

/** 我的文章(含草稿/已下架)。RLS 限本人;可按 status 筛。 */
export function useMyArticles(args: { status?: ArticleStatus; enabled?: boolean } = {}) {
  const { status, enabled = true } = args
  return useQuery({
    queryKey: ['article', 'mine', { status: status ?? null }] as const,
    enabled,
    queryFn: async (): Promise<ArticleMineItem[]> => {
      const sb = getSupabase()
      let q = sb
        .from('v_article_mine')
        .select(MINE_COLS)
        .order('updated_at', { ascending: false })
      if (status) q = q.eq('status', status)
      const { data, error } = await q
      if (error) throw error
      return (data ?? []) as ArticleMineItem[]
    },
  })
}

/** 编辑器加载单篇(本人,含草稿正文)走 v_article_mine 按 id。 */
export function useMyArticle(id: string | undefined) {
  return useQuery({
    queryKey: ['article', 'mine-one', id] as const,
    enabled: Boolean(id),
    queryFn: async (): Promise<ArticleMineItem | null> => {
      const sb = getSupabase()
      const { data, error } = await sb
        .from('v_article_mine')
        .select(MINE_COLS)
        .eq('id', id!)
        .maybeSingle()
      if (error) throw error
      return (data as ArticleMineItem | null) ?? null
    },
  })
}

export type CreateArticleInput = {
  title: string
  content: JSONContent
  excerpt?: string | null
  cover_file_info_id?: string | null
  category_id?: string | null
  status: 'draft' | 'published'
}

export type UpdateArticleInput = Partial<CreateArticleInput> & { id: string }

type WriteArticleResp = { ok: boolean; id: string }

export function useCreateArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateArticleInput) =>
      apiClient.post<WriteArticleResp>('/gapi/user/articles', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['article'] }),
  })
}

export function useUpdateArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateArticleInput) =>
      apiClient.patch<WriteArticleResp>(`/gapi/user/articles/${id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['article'] }),
  })
}

export function useDeleteArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.del<WriteArticleResp>(`/gapi/user/articles/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['article'] }),
  })
}

// ---- 嵌入解析 ----

export type PublicFileMeta = {
  id: string
  user_id: string
  base_file_id: string | null
  name: string
  type: string
  size_original: number | null
  mime_type: string | null
  created_at: string
  thumb_url_list: string | null
  thumb_url_feed: string | null
  duration_ms: number | null
  media_width: number | null
  media_height: number | null
  owner_username: string | null
  owner_nickname: string | null
  owner_avatar: string | null
}

/**
 * 批量按 uuid 解析公开文件(嵌入 fileRef + /preview/:id 共用)。
 * 走 security-definer RPC get_public_files(不可枚举:必须传 id;函数体自带
 * is_public/active/非 folder 过滤)。命中可能少于传入 id 数 → 调用方按缺失降级。
 * get:true → PostgREST 走 GET(函数已 STABLE),经 supabase 负载均衡时可分流 read replica。
 */
export function useResolvePublicFiles(ids: string[], enabled = true) {
  const sorted = [...ids].sort()
  return useQuery({
    queryKey: ['public-files', 'resolve', sorted] as const,
    enabled: enabled && sorted.length > 0,
    queryFn: async (): Promise<PublicFileMeta[]> => {
      const sb = getSupabase()
      const res = (await sb.rpc('get_public_files', { p_ids: sorted }, { get: true })) as {
        data: PublicFileMeta[] | null
        error: { message: string } | null
      }
      if (res.error) throw new Error(res.error.message)
      return res.data ?? []
    },
  })
}

export type PublicPackMeta = {
  id: string
  name: string
  description: string | null
  price_coin: number | null
  cover_base_file_id: string | null
  cover_name: string | null
  cover_thumb_url_feed: string | null
  item_count: number | null
}

/** 批量按 uuid 解析公开资源包(嵌入 packRef)。走 v_resource_pack_public_meta。 */
export function useResolvePublicPacks(ids: string[], enabled = true) {
  const sorted = [...ids].sort()
  return useQuery({
    queryKey: ['public-packs', 'resolve', sorted] as const,
    enabled: enabled && sorted.length > 0,
    queryFn: async (): Promise<PublicPackMeta[]> => {
      const sb = getSupabase()
      const { data, error } = await sb
        .from('v_resource_pack_public_meta')
        .select('id,name,description,price_coin,cover_base_file_id,cover_name,cover_thumb_url_feed,item_count')
        .in('id', sorted)
      if (error) throw error
      return (data ?? []) as PublicPackMeta[]
    },
  })
}
